import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";

import Api from "src/Config";

import { useDebounce } from "src/hooks/useDebonce";
import { textTruncate } from "src/helpers/truncateText";

export const FraudAlertContext = createContext(null);

type Props = {
  children: ReactElement;
};

const FraudAlertProvider: FunctionComponent<Props> = ({ children }) => {
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const debouncedValue = useDebounce<string>(searchParam, 500);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["fraud_alert", debouncedValue, pageNumber],
    staleTime: 30000,
    queryFn: async () => {
      const url = debouncedValue
        ? `tm/alerts?search=${debouncedValue}&page_size=10&page=1`
        : `tm/alerts?page_size=10&page=${pageNumber + 1}`;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          transaction: {
            id: string;
            reference: string;
            amount: string;
            score: number;
            severity: string;
            status: string;
            user: {
              last_name: string;
              first_name: string;
            };
          };
        }) => ({
          id: res.id,
          refId: res.transaction.id,
          "transaction ID": res.transaction.reference,
          name: `${
            !res.transaction?.user?.first_name?.toLowerCase() &&
            !res.transaction?.user?.last_name?.toLowerCase()
              ? "N/A"
              : textTruncate(
                  `${res.transaction.user.first_name?.toLowerCase() || ""} ${
                    res.transaction.user.last_name?.toLowerCase() || ""
                  }`,
                  20,
                )
          }`,
          amount: res.transaction.amount,
          "fraud score": res.transaction.score,
          status: res.transaction.status,
          severity: res.transaction.severity || "N/A",
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const handleSearch = (e: any) => {
    setSearchParam(e.target.value);
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
    ],
  );
  return (
    <FraudAlertContext.Provider value={contextValue}>
      {children}
    </FraudAlertContext.Provider>
  );
};

export default FraudAlertProvider;
